import * as XLSX from "xlsx-js-style";
import type { PropertyAttributeRow, AttributeField } from "@/features/properties/types/propertyAttributes";
import { getNumeration } from "./numbering";
import { collectEvidenceUrls, getChildren } from "./treeHelpers";
import { downloadWorkbook } from "../_xlsx";

/**
 * Creates and downloads an Excel file (.xlsx) with one tab per property,
 * flattening the nested attributes JSON into questions and answers.
 */
export function exportPropertiesToExcel(
  propertyAttributes: PropertyAttributeRow[],
  properties: any[],
  projectName: string
) {
  const wb = XLSX.utils.book_new();

  // Sort properties by name
  const sortedProperties = [...properties].sort((a, b) =>
    (a.name || a.address || "").localeCompare(b.name || b.address || "")
  );

  interface QuestionSummary {
    category: string;
    question: string;
    answers: any[];
  }
  const summaryAggregation = new Map<string, QuestionSummary>();

  for (const property of sortedProperties) {
    const attributeRow = propertyAttributes.find((pa) => pa.property_id === property.id);

    // Default empty sheet if no data
    const wsData: any[][] = [
      ["Category", "Question", "Answer", "Evidence"]
    ];

    if (attributeRow && attributeRow.data) {
      let fields: AttributeField[] = [];
      if (typeof attributeRow.data === "string") {
        try {
          fields = JSON.parse(attributeRow.data);
        } catch (e) {
          console.error("Failed to parse attribute data", e);
        }
      } else {
        fields = attributeRow.data;
      }

      function processAttributeField(field: AttributeField, parentTitle: string, cleanParentTitle: string, pathIndices: number[]) {
        const depth = pathIndices.length - 1;
        const index = pathIndices[depth];
        const numeration = getNumeration(depth, index);
        const titleWithNum = `${numeration} ${field.title}`;

        const children = getChildren(field);
        if (children) {
          const currentParentTitle = parentTitle ? `${parentTitle} > ${titleWithNum}` : titleWithNum;
          const currentCleanParentTitle = cleanParentTitle ? `${cleanParentTitle} > ${field.title}` : field.title;
          children.forEach((child, idx) => {
            processAttributeField(child, currentParentTitle, currentCleanParentTitle, [...pathIndices, idx]);
          });
        } else {
          // Leaf — type-narrow by discriminator
          let answerValue = "";
          switch (field.type) {
            case "photos":
              answerValue = field.value && field.value.length > 0
                ? `${field.value.length} photo(s) attached`
                : "No photos";
              break;
            case "area":
              answerValue = field.value ? "HTML content present" : "N/A";
              break;
            case "multiSelect":
              answerValue = field.value && field.value.length > 0 ? field.value.join(", ") : "N/A";
              break;
            case "input":
            case "select": {
              const val = field.value;
              answerValue = val === null || val === undefined ? "N/A" : String(val);
              break;
            }
            case "node":
              answerValue = "N/A";
              break;
            default: {
              // Leaf types the union omits (date/number/text/switch/…) — stringify
              // the value instead of blanking the cell.
              const v = (field as { value?: unknown }).value;
              answerValue = v == null ? "" : String(v);
              break;
            }
          }

          // Tab-specific (with numeration)
          const tabCategory = parentTitle.split(" > ")[0] || "General";
          const tabQuestion = titleWithNum.replace(/\s*\[.*?\]\s*$/, "");
          const evidenceValue = collectEvidenceUrls(field).join("\n");
          wsData.push([tabCategory, tabQuestion, answerValue, evidenceValue]);

          // Summary-specific (clean, no numeration)
          const cleanCategory = cleanParentTitle.split(" > ")[0] || "General";
          const cleanQuestion = field.title.replace(/\s*\[.*?\]\s*$/, "");

          const key = `${cleanCategory}|||${cleanQuestion}`;
          if (!summaryAggregation.has(key)) {
            summaryAggregation.set(key, { category: cleanCategory, question: cleanQuestion, answers: [] });
          }
          if (answerValue !== "N/A" && answerValue !== "No photos" && answerValue !== "") {
            summaryAggregation.get(key)!.answers.push(answerValue);
          }
        }
      }

      fields.forEach((field, idx) => {
        processAttributeField(field, "", "", [idx]);
      });
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Styling
    ws["!cols"] = [{ wch: 25 }, { wch: 40 }, { wch: 50 }, { wch: 80 }];

    for (let r = 0; r < wsData.length; r++) {
      for (let c = 0; c < 4; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!ws[cellRef]) continue;

        ws[cellRef].s = {
          alignment: { wrapText: true, vertical: "top" }
        };

        if (r === 0) { // Header row
          ws[cellRef].s.font = { color: { rgb: "FFFFFF" }, bold: true };
          ws[cellRef].s.fill = { fgColor: { rgb: "000000" } };
        }
      }
    }

    // Tab name limit is 31 characters, remove invalid characters
    let tabName = (property.name || property.address || `Prop-${property.id.slice(0,4)}`)
      .substring(0, 31)
      .replace(/[\\/?*[\]:]/g, "");

    // Ensure unique tab names if duplicate
    let i = 1;
    const originalTabName = tabName;
    while (wb.SheetNames.includes(tabName)) {
      const suffix = ` (${i})`;
      tabName = originalTabName.substring(0, 31 - suffix.length) + suffix;
      i++;
    }

    XLSX.utils.book_append_sheet(wb, ws, tabName);
  }

  // Generate Summary Tab
  const summaryData: any[][] = [
    [`Project: ${projectName} — Summary Across All Stores`, "", "", "", ""],
    [`Stores included: ${sortedProperties.length} (${sortedProperties.map(p => p.name || p.address || p.id.slice(0,6)).join(", ")})`, "", "", "", ""],
    ["Category", "Question", "# Responses", "Summary", "Distribution of Answers"]
  ];

  let currentCategory = "";
  for (const sum of Array.from(summaryAggregation.values())) {
    if (sum.category !== currentCategory) {
      summaryData.push([sum.category, "", "", "", ""]);
      currentCategory = sum.category;
    }

    const validAnswers = sum.answers;
    const count = validAnswers.length;

    let summaryText = "";
    let distributionText = "";

    if (count === 0) {
      summaryText = "All N/A";
      distributionText = `All ${sortedProperties.length} stores: N/A`;
    } else {
      const freqMap = new Map<string, number>();
      let allNumeric = true;
      const numValues: number[] = [];

      for (const ans of validAnswers) {
        const strAns = String(ans).trim();
        freqMap.set(strAns, (freqMap.get(strAns) || 0) + 1);

        const num = Number(strAns);
        if (isNaN(num) || strAns === "") {
          allNumeric = false;
        } else {
          numValues.push(num);
        }
      }

      const sortedFreq = Array.from(freqMap.entries()).sort((a, b) => b[1] - a[1]);
      distributionText = sortedFreq.map(([val, freq]) => `${val} (${freq})`).join("; ");

      if (allNumeric && numValues.length > 0) {
        numValues.sort((a, b) => a - b);
        const sumVal = numValues.reduce((a, b) => a + b, 0);
        const avg = (sumVal / numValues.length).toFixed(1);
        const min = numValues[0];
        const max = numValues[numValues.length - 1];
        const mid = Math.floor(numValues.length / 2);
        const median = numValues.length % 2 === 0 ? (numValues[mid - 1] + numValues[mid]) / 2 : numValues[mid];

        summaryText = `Avg: ${avg} | Median: ${median} | Range: ${min}-${max} | Total: ${sumVal}`;
      } else {
        const mostCommon = sortedFreq[0];
        const percentage = Math.round((mostCommon[1] / count) * 100);
        summaryText = `Most common: ${mostCommon[0]} (${mostCommon[1]}/${count}, ${percentage}%)`;
      }
    }

    summaryData.push([sum.category, sum.question, count, summaryText, distributionText]);
  }

  if (summaryData.length > 3) {
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary["!cols"] = [{ wch: 25 }, { wch: 50 }, { wch: 12 }, { wch: 50 }, { wch: 60 }];
    wsSummary["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }, // Title row
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } }  // Stores list
    ];

    // Apply formatting
    for (let r = 0; r < summaryData.length; r++) {
      const rowData = summaryData[r];
      const isHeader = r === 0 || r === 2;
      const isStoresIncluded = r === 1;
      const isCategory = r >= 3 && rowData[0] !== "" && rowData[1] === "";

      for (let c = 0; c < 5; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!wsSummary[cellRef]) continue;

        wsSummary[cellRef].s = {
          alignment: { wrapText: true, vertical: "top" }
        };

        if (isHeader || isCategory) {
          wsSummary[cellRef].s.font = { color: { rgb: "FFFFFF" }, bold: true };
          wsSummary[cellRef].s.fill = { fgColor: { rgb: "000000" } };
        } else if (isStoresIncluded) {
          wsSummary[cellRef].s.font = { italic: true };
        }
      }

      if (isCategory) {
        wsSummary["!merges"].push({ s: { r, c: 0 }, e: { r, c: 4 } });
      }
    }

    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
    wb.SheetNames.unshift(wb.SheetNames.pop() as string);
  }

  if (wb.SheetNames.length === 0) {
    const ws = XLSX.utils.aoa_to_sheet([["No Properties Available"]]);
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  }

  const safeProjectName = projectName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
  downloadWorkbook(wb, `${safeProjectName}_site_survey_data.xlsx`);
}
